import Box from '@components/base/Box'
import Modal, { ModalProps } from '@components/base/Modal'
import { Button } from '@components/inputs/Button'
import { OrderData } from '@components/metrics/TableOrders'
import { useTheme } from '@libs/useTheme'
import React, { useEffect, useMemo, useState } from 'react'
import {
  MdCheck,
  MdChevronLeft,
  MdChevronRight,
  MdContentCopy,
  MdOutlineConfirmationNumber,
  MdOutlinePayments,
} from 'react-icons/md'

type OrderModalProps = {
  data?: OrderData
  onComplete: (data: OrderData) => void
} & Omit<ModalProps, 'children'>

type TicketTypeSelection = {
  selectedTitle: string
  options: Array<{ title: string; isUnavailable?: boolean }>
}

export const getTicketTypeSelection = (
  orderedTicketTitle: string,
  eventTicketTypes: ReadonlyArray<{ title: string }>
): TicketTypeSelection => {
  const normalizedOrderedTitle = orderedTicketTitle.trim().toUpperCase()
  const matchingEventTicket = eventTicketTypes.find(
    (ticket) => ticket.title.trim().toUpperCase() === normalizedOrderedTitle
  )
  const eventOptions = eventTicketTypes.map(({ title }) => ({ title }))

  if (matchingEventTicket) {
    return {
      selectedTitle: matchingEventTicket.title,
      options: eventOptions,
    }
  }

  return {
    selectedTitle: orderedTicketTitle,
    options: orderedTicketTitle
      ? [{ title: orderedTicketTitle, isUnavailable: true }, ...eventOptions]
      : eventOptions,
  }
}

const paymentStatusStyles: Record<string, string> = {
  SUCCEEDED: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  PENDING: 'bg-amber-100 text-amber-900 border-amber-200',
  FAILED: 'bg-red-100 text-red-800 border-red-200',
  REFUNDED: 'bg-slate-200 text-slate-800 border-slate-300',
  CHARGED_BACK: 'bg-red-100 text-red-800 border-red-200',
}

const Identifier = ({
  label,
  value,
  copied,
  onCopy,
}: {
  label: string
  value?: string | null
  copied: boolean
  onCopy: () => void
}) => (
  <Box className="min-w-0">
    <Box className="text-xs font-semibold uppercase tracking-wide opacity-60">
      {label}
    </Box>
    <Box className="mt-1 flex min-w-0 items-center gap-2">
      <code
        className="truncate text-xs sm:text-sm"
        title={value ?? 'Not available'}
      >
        {value ?? 'Not available'}
      </code>
      {!!value && (
        <button
          type="button"
          aria-label={`Copy ${label}`}
          title={`Copy ${label}`}
          className="shrink-0 cursor-pointer rounded-md p-1.5 opacity-70 transition-colors hover:bg-black/5 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forumm-blue"
          onClick={onCopy}
        >
          {copied ? (
            <MdCheck aria-hidden="true" className="h-4 w-4" />
          ) : (
            <MdContentCopy aria-hidden="true" className="h-4 w-4" />
          )}
        </button>
      )}
    </Box>
  </Box>
)

const OrderModal = ({
  data,
  onComplete,
  className,
  setShow,
  ...modalProps
}: OrderModalProps) => {
  const { theme } = useTheme()
  const [modalData, setModalData] = useState<OrderData | undefined>(data)
  const [activeTicketIndex, setActiveTicketIndex] = useState(0)
  const [copiedIdentifier, setCopiedIdentifier] = useState<
    'order' | 'event' | undefined
  >()

  useEffect(() => {
    setModalData(data)
    setActiveTicketIndex(0)
    setCopiedIdentifier(undefined)
  }, [data])

  const tickets = modalData?.tickets ?? []
  const activeTicket = tickets[activeTicketIndex]
  const paymentStatus =
    modalData?.paymentStatus ?? (modalData?.paid ? 'SUCCEEDED' : 'PENDING')
  const paymentProcessor = modalData?.paymentProcessor ?? 'Legacy payment'
  const availableTicketTypes = modalData?.event?.tickets ?? []
  const ticketTypeSelection = getTicketTypeSelection(
    activeTicket?.title ?? '',
    availableTicketTypes
  )

  const surfaceStyle = useMemo(
    () => ({
      backgroundColor: theme.backgroundColourSecondary,
      borderColor: theme.borderColour,
      color: theme.textColour,
    }),
    [theme]
  )

  const copyIdentifier = async (
    type: 'order' | 'event',
    value?: string | null
  ) => {
    if (!value) return
    try {
      await navigator.clipboard.writeText(value)
      setCopiedIdentifier(type)
      window.setTimeout(() => setCopiedIdentifier(undefined), 1500)
    } catch (_) {
      // Clipboard access may be blocked by browser permissions.
    }
  }

  const updateActiveTicket = (
    changes: Partial<NonNullable<OrderData['tickets']>[number]>
  ) => {
    setModalData((current) => {
      if (!current) return current
      const updatedTickets = [...(current.tickets ?? [])]
      if (!updatedTickets[activeTicketIndex]) return current
      updatedTickets[activeTicketIndex] = {
        ...updatedTickets[activeTicketIndex],
        ...changes,
      }
      return { ...current, tickets: updatedTickets }
    })
  }

  const closeModal = () => setShow?.(false)

  const saveOrder = () => {
    if (!modalData) return
    onComplete(modalData)
    setActiveTicketIndex(0)
    closeModal()
  }

  return (
    <Modal
      {...modalProps}
      setShow={setShow}
      className={`!max-w-5xl md:!min-w-0 ${className ?? ''} rounded-md`}
    >
      {!modalData ? (
        <Box className="py-12 text-center text-sm opacity-70">
          Select an order to manage it.
        </Box>
      ) : (
        <Box className="flex flex-col gap-5 pb-2">
          <section
            aria-label="Order summary"
            className="rounded-xl border p-4 sm:p-5"
            style={surfaceStyle}
          >
            <Box className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <Box className="min-w-0">
                <Box className="text-xs font-semibold uppercase tracking-wide opacity-60">
                  Event
                </Box>
                <Box className="mt-1 truncate text-xl font-semibold sm:text-2xl">
                  {modalData.event?.event?.title ?? 'Event unavailable'}
                </Box>
                <Box className="mt-3 flex flex-wrap gap-2">
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${
                      paymentStatusStyles[paymentStatus] ??
                      paymentStatusStyles.PENDING
                    }`}
                  >
                    <MdOutlinePayments aria-hidden="true" className="h-4 w-4" />
                    {paymentStatus.replaceAll('_', ' ')}
                  </span>
                  <span className="inline-flex items-center rounded-full border border-slate-300 bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-800">
                    {paymentProcessor}
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-800">
                    <MdOutlineConfirmationNumber
                      aria-hidden="true"
                      className="h-4 w-4"
                    />
                    {tickets.length}{' '}
                    {tickets.length === 1 ? 'ticket' : 'tickets'}
                  </span>
                </Box>
              </Box>

              <Box className="grid min-w-0 gap-4 sm:grid-cols-2 lg:w-[48%]">
                <Identifier
                  label="Order ID"
                  value={modalData.orderId}
                  copied={copiedIdentifier === 'order'}
                  onCopy={() => copyIdentifier('order', modalData.orderId)}
                />
                <Identifier
                  label="Event ID"
                  value={modalData.eventId}
                  copied={copiedIdentifier === 'event'}
                  onCopy={() => copyIdentifier('event', modalData.eventId)}
                />
              </Box>
            </Box>

            <Box
              className="mt-5 flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between"
              style={{ borderColor: theme.borderColour }}
            >
              <Box>
                <Box className="text-sm font-semibold">
                  Manual paid override
                </Box>
                <Box className="mt-0.5 text-xs opacity-70">
                  Only change this after confirming payment outside Forumm.
                </Box>
              </Box>
              <button
                type="button"
                role="switch"
                aria-checked={!!modalData.paid}
                className="flex cursor-pointer items-center gap-3 self-start rounded-lg px-2 py-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forumm-blue sm:self-auto"
                onClick={() =>
                  setModalData((current) =>
                    current ? { ...current, paid: !current.paid } : current
                  )
                }
              >
                <span className="text-sm font-medium">
                  {modalData.paid ? 'Marked paid' : 'Not marked paid'}
                </span>
                <span
                  aria-hidden="true"
                  className={`relative h-6 w-11 rounded-full transition-colors ${
                    modalData.paid ? 'bg-emerald-600' : 'bg-slate-400'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
                      modalData.paid ? '-translate-x-5' : 'translate-x-0.5'
                    }`}
                  />
                </span>
              </button>
            </Box>
          </section>

          {tickets.length > 0 && (
            <section
              aria-label="Ticket holders"
              className="grid overflow-hidden rounded-xl border lg:grid-cols-[260px_minmax(0,1fr)]"
              style={{ borderColor: theme.borderColour }}
            >
              <Box
                className="border-b p-3 lg:border-b-0 lg:border-r"
                style={surfaceStyle}
              >
                <Box className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide opacity-60">
                  Ticket holders
                </Box>
                <Box className="flex gap-2 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible">
                  {tickets.map((ticket, index) => {
                    const isActive = index === activeTicketIndex
                    return (
                      <button
                        type="button"
                        key={ticket.ticketCode ?? `${ticket.email}-${index}`}
                        aria-current={isActive ? 'step' : undefined}
                        className={`min-w-[210px] cursor-pointer rounded-lg border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forumm-blue lg:min-w-0 ${
                          isActive
                            ? 'border-forumm-blue bg-forumm-blue/10'
                            : 'border-transparent hover:bg-black/5'
                        }`}
                        onClick={() => setActiveTicketIndex(index)}
                      >
                        <Box className="flex items-center justify-between gap-2">
                          <span className="text-xs font-semibold opacity-60">
                            Ticket {index + 1}
                          </span>
                          {isActive && (
                            <MdCheck
                              aria-hidden="true"
                              className="h-4 w-4 text-forumm-blue"
                            />
                          )}
                        </Box>
                        <Box className="mt-1 truncate text-sm font-semibold">
                          {ticket.fullName || 'Unnamed attendee'}
                        </Box>
                        <Box className="mt-0.5 truncate text-xs opacity-70">
                          {ticket.isAdditionalGuest
                            ? 'Managed by registrar'
                            : ticket.email}
                        </Box>
                      </button>
                    )
                  })}
                </Box>
              </Box>

              {!!activeTicket && (
                <Box className="p-4 sm:p-6">
                  <Box className="mb-5 flex items-start justify-between gap-3">
                    <Box>
                      <Box className="text-lg font-semibold">
                        Ticket {activeTicketIndex + 1} details
                      </Box>
                      <Box className="mt-1 text-sm opacity-70">
                        Update the attendee details or assign another ticket
                        type.
                      </Box>
                    </Box>
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-800">
                      {activeTicket.title}
                    </span>
                  </Box>

                  <Box className="grid gap-4 md:grid-cols-2">
                    <label className="flex flex-col gap-1.5 text-sm font-medium">
                      Full name <span className="sr-only">required</span>
                      <input
                        required
                        type="text"
                        value={activeTicket.fullName}
                        placeholder="John Smith"
                        className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition-colors placeholder:text-slate-500 focus:border-forumm-blue focus:ring-2 focus:ring-forumm-blue/20"
                        onChange={(event) =>
                          updateActiveTicket({ fullName: event.target.value })
                        }
                      />
                    </label>

                    <label className="flex flex-col gap-1.5 text-sm font-medium">
                      Ticket type <span className="sr-only">required</span>
                      <select
                        required
                        value={ticketTypeSelection.selectedTitle}
                        disabled={availableTicketTypes.length === 0}
                        className="h-11 w-full cursor-pointer rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition-colors focus:border-forumm-blue focus:ring-2 focus:ring-forumm-blue/20 disabled:cursor-not-allowed disabled:bg-slate-100"
                        onChange={(event) =>
                          updateActiveTicket({ title: event.target.value })
                        }
                      >
                        {ticketTypeSelection.options.map((ticket, index) => (
                          <option
                            key={`${ticket.title}-${index}`}
                            value={ticket.title}
                          >
                            {ticket.title}
                            {ticket.isUnavailable
                              ? ' (not currently configured)'
                              : ''}
                          </option>
                        ))}
                      </select>
                    </label>
                  </Box>

                  {!activeTicket.isAdditionalGuest && (
                    <label className="mt-4 flex flex-col gap-1.5 text-sm font-medium">
                      Email address <span className="sr-only">required</span>
                      <input
                        required
                        type="email"
                        value={activeTicket.email ?? ''}
                        disabled={activeTicketIndex === 0}
                        placeholder="attendee@forumm.to"
                        className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition-colors placeholder:text-slate-500 focus:border-forumm-blue focus:ring-2 focus:ring-forumm-blue/20 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-600"
                        onChange={(event) =>
                          updateActiveTicket({ email: event.target.value })
                        }
                      />
                      {activeTicketIndex === 0 && (
                        <span className="text-xs font-normal opacity-70">
                          The primary attendee email cannot be changed.
                        </span>
                      )}
                    </label>
                  )}

                  {activeTicket.discountCode && (
                    <Box className="mt-4 rounded-lg border p-3">
                      <Box className="text-xs font-semibold uppercase tracking-wide opacity-60">
                        Discount code
                      </Box>
                      <Box className="mt-1 font-mono text-sm">
                        {activeTicket.discountCode}
                      </Box>
                    </Box>
                  )}

                  {tickets.length > 1 && (
                    <Box
                      className="mt-6 flex items-center justify-between border-t pt-4"
                      style={{ borderColor: theme.borderColour }}
                    >
                      <button
                        type="button"
                        disabled={activeTicketIndex === 0}
                        className="inline-flex cursor-pointer items-center gap-1 rounded-lg px-2 py-1.5 text-sm font-medium transition-colors hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forumm-blue disabled:cursor-not-allowed disabled:opacity-40"
                        onClick={() =>
                          setActiveTicketIndex((index) =>
                            Math.max(0, index - 1)
                          )
                        }
                      >
                        <MdChevronLeft aria-hidden="true" className="h-5 w-5" />
                        Previous
                      </button>
                      <Box className="text-xs opacity-60">
                        {activeTicketIndex + 1} of {tickets.length}
                      </Box>
                      <button
                        type="button"
                        disabled={activeTicketIndex === tickets.length - 1}
                        className="inline-flex cursor-pointer items-center gap-1 rounded-lg px-2 py-1.5 text-sm font-medium transition-colors hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forumm-blue disabled:cursor-not-allowed disabled:opacity-40"
                        onClick={() =>
                          setActiveTicketIndex((index) =>
                            Math.min(tickets.length - 1, index + 1)
                          )
                        }
                      >
                        Next
                        <MdChevronRight
                          aria-hidden="true"
                          className="h-5 w-5"
                        />
                      </button>
                    </Box>
                  )}
                </Box>
              )}
            </section>
          )}

          <Box
            className="flex flex-col-reverse gap-3 border-t pt-4 sm:flex-row sm:justify-end"
            style={{ borderColor: theme.borderColour }}
          >
            <Button
              title="Cancel"
              type="secondary"
              size="medium"
              className="w-full sm:w-auto"
              onClick={closeModal}
            />
            <Button
              title="Save order"
              type="primary"
              size="medium"
              className="w-full sm:w-auto"
              disabled={!modalData}
              onClick={saveOrder}
            />
          </Box>
        </Box>
      )}
    </Modal>
  )
}

export default OrderModal
